'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { GetManyRequestType } from '@shared-types/common';
import type { DocPabean, DocPabeanResponse } from '../models/doc-pabean-model';

type DocPabeanQueryParams = GetManyRequestType & {
  orderBy?: string;
  ascending?: string;
  statusFilter?: string;
  /** Include soft-deleted documents in the list. Defaults false. */
  includeDeleted?: boolean;
  /**
   * Branch (cabang) to list. Empty is NOT "no filter" — it means "my own
   * branch", which the API resolves from the session. Send BRANCH_ALL to see
   * every branch.
   */
  branchFilter?: string;
};

type DocPabeanQueryResult = {
  data: DocPabean[];
  totalCount: number;
  page: number;
  /** Parts of the page the API could not load (backend#1916). */
  incomplete: string[];
};

async function fetchDocPabeans({
  page = 1,
  itemPerPage = 10,
  searchQuery = '',
  orderBy = 'id',
  ascending = 'false',
  statusFilter = '',
  includeDeleted = false,
  branchFilter = '',
  signal,
}: DocPabeanQueryParams & { signal: AbortSignal }): Promise<DocPabeanQueryResult> {
  try {
    const offset = (Math.max(1, page) - 1) * itemPerPage;

    const params: Record<string, string> = {
      offset: offset.toString(),
      limit: itemPerPage.toString(),
      orderBy,
      ascending,
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    if (statusFilter) {
      params.respon = statusFilter;
    }

    // Opt-in only: without it the API returns live rows exactly as before.
    if (includeDeleted) {
      params.includeDeleted = 'true';
    }

    // Omitted on purpose when empty: the API reads an absent `branch` as the
    // caller's own branch. Sending branch='' would be a different request.
    if (branchFilter) {
      params.branch = branchFilter;
    }

    const { data: resultResponse } = await axiosInstance.get<{
      data: DocPabeanResponse;
    }>(apiUrl.docPabean.list, {
      signal,
      params,
    });

    return {
      data: resultResponse.data.data,
      totalCount: resultResponse.data.totalCount,
      page: resultResponse.data.page,
      incomplete: resultResponse.data.incomplete ?? [],
    };
  } catch (error: any) {
    if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
      throw error;
    }

    const message = error?.response?.data?.message || error?.message || 'Request failed!';
    throw new Error(message);
  }
}

export default function useGetDocPabeans(params: DocPabeanQueryParams) {
  const {
    page = 1,
    itemPerPage = 10,
    searchQuery = '',
    orderBy = 'id',
    ascending = 'false',
    statusFilter = '',
    includeDeleted = false,
    branchFilter = '',
  } = params;

  return useSuspenseQuery({
    queryKey: [
      'doc-pabeans',
      {
        page: Math.max(1, page),
        itemPerPage,
        searchQuery,
        orderBy,
        ascending,
        statusFilter,
        includeDeleted,
        // Part of the key, not just the request: without it a branch switch
        // would be served the previous branch's rows from cache.
        branchFilter,
      },
    ],
    queryFn: ({ signal }) =>
      fetchDocPabeans({
        page,
        itemPerPage,
        searchQuery,
        orderBy,
        ascending,
        statusFilter,
        includeDeleted,
        branchFilter,
        signal,
      }),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    // Refetch when the page is opened — same reason as the draft list: with
    // this false and a 5-minute staleTime, a document that had just changed
    // state did not show up until the cache expired or the browser was
    // hard-refreshed.
    refetchOnMount: 'always',
    refetchOnReconnect: false,
  });
}
